/**
 * Brevo Target List Selector (P0 iter 120)
 *
 * Dropdown that fetches the user's Brevo lists from
 * GET /api/integrations/brevo/lists and lets them pick the target list
 * used for prospect synchronization.
 *
 * Behaviour:
 *  - Auto-fetches lists on mount.
 *  - Displays a "Refresh" button.
 *  - Displays a clear hint if Brevo is not configured yet.
 *  - On change, calls the parent `onChange(listId)` so the existing
 *    save flow includes the value in `target_list_id`.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, AlertCircle, ListPlus } from 'lucide-react';
import { apiClient } from '@/lib/api';

export default function BrevoTargetListSelector({ value, onChange }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(value ?? null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
    valueRef.current = value;
  }, [onChange, value]);

  useEffect(() => {
    setSelectedId(value ?? null);
  }, [value]);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/integrations/brevo/lists');
      setLists(res.data?.lists || []);
      const remoteSelected = res.data?.selected_id ?? null;
      if (remoteSelected != null && (valueRef.current == null || valueRef.current === undefined)) {
        setSelectedId(remoteSelected);
        onChangeRef.current?.(remoteSelected);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      if (err?.response?.status === 400) {
        setError('Saisissez la clé API Brevo et enregistrez d\'abord la configuration pour charger vos listes.');
      } else {
        setError(detail || 'Impossible de récupérer les listes Brevo.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const handleSelect = (e) => {
    const raw = e.target.value;
    const id = raw === '' ? null : Number(raw);
    setSelectedId(id);
    onChange?.(id);
  };

  return (
    <div data-testid="brevo-target-list-selector">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-semantic flex items-center gap-1.5">
          <ListPlus className="w-4 h-4" />
          Liste cible Brevo <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          onClick={fetchLists}
          disabled={loading}
          className="text-xs flex items-center gap-1 text-semantic-muted hover:text-semantic"
          data-testid="brevo-refresh-lists-btn"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Actualiser les listes
        </button>
      </div>

      <select
        value={selectedId ?? ''}
        onChange={handleSelect}
        disabled={loading || lists.length === 0}
        className="w-full input-semantic"
        data-testid="brevo-target-list-select"
      >
        <option value="">— Choisir une liste —</option>
        {lists.map((lst) => (
          <option key={lst.id} value={lst.id}>
            {lst.name} (#{lst.id}) — {lst.total_subscribers || 0} contacts
          </option>
        ))}
      </select>

      {error && (
        <div
          className="mt-2 text-xs flex items-start gap-1.5"
          style={{ color: 'hsl(40, 100%, 35%)' }}
          data-testid="brevo-list-error"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!error && !loading && lists.length === 0 && (
        <div className="mt-2 text-xs text-semantic-muted">
          Aucune liste trouvée. Créez-en une dans Brevo puis cliquez sur « Actualiser ».
        </div>
      )}

      {!selectedId && lists.length > 0 && (
        <div className="mt-2 text-xs" style={{ color: 'hsl(0, 70%, 45%)' }}>
          Vous devez sélectionner une liste cible avant d'enregistrer.
        </div>
      )}
    </div>
  );
}
